import type { SxProps } from '@mui/material';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useConfirmationDialog } from '@/shared/hooks';
import { CopyButton } from '../CopyButton/CopyButton';
import { TrashIcon } from '../Icons/TrashIcon/TrashIcon';

interface Props {
  sx?: SxProps;
  slotProps?: {
    fields?: {
      sx?: SxProps;
    };
    field?: {
      sx?: SxProps;
    };
  };
  recordNoun: string;
  title: string;
  fields?: {
    label: string;
    value: string;
    withCopyButton?: boolean;
  }[];
  actions?: ReactNode;
  children?: ReactNode;
  onDelete: () => Promise<void>;
}

export function DataRecordCard({
  sx,
  slotProps,
  recordNoun,
  title,
  fields,
  actions,
  children,
  onDelete,
}: Props) {
  const { openDialog, dialogElememnt } = useConfirmationDialog({
    title: `Delete ${recordNoun}`,
    description: `Are you sure that you want to delete "${title}" ${recordNoun}?`,
    acceptButtonColor: 'error',
    acceptButtonText: 'Delete',
    onAccept: onDelete,
  });

  return (
    <>
      <Box sx={{ border: '1px solid #DDDFE7', borderRadius: 2, ...sx }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: '1.125rem',
              lineHeight: '1.375rem',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={title}
          >
            {title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {actions}
            <IconButton sx={{ p: 0 }} onClick={openDialog}>
              <TrashIcon />
            </IconButton>
          </Box>
        </Box>
        <Divider sx={{ '&:last-child': { display: 'none' } }} />
        {fields && fields.length > 0 && (
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              overflow: 'auto',
              ...slotProps?.fields?.sx,
            }}
          >
            {fields.map((field) => {
              return (
                <Typography
                  key={field.label}
                  component="span"
                  sx={{
                    flex: 1,
                    fontSize: '0.875rem',
                    lineHeight: '1.125rem',
                    display: 'flex',
                    alignItems: 'center',
                    maxWidth: 280,
                    minWidth: 0,
                    ...slotProps?.field?.sx,
                  }}
                >
                  <span
                    style={{
                      color: '#7E828F',
                      whiteSpace: 'nowrap',
                      marginRight: 4,
                    }}
                  >
                    {field.label}:{' '}
                  </span>
                  <span
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}
                    title={field.value}
                  >
                    {field.value}
                  </span>
                  {field.withCopyButton && (
                    <CopyButton
                      sx={{ ml: 0.5, my: -0.875 }}
                      copyContent={field.value}
                    />
                  )}
                </Typography>
              );
            })}
          </Box>
        )}
        {children}
      </Box>
      {dialogElememnt}
    </>
  );
}
